import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Seo } from 'components'
import { Form } from 'forms/Form'
import { useActions } from 'hooks'
import { useIsMobile } from 'hooks/media'
import { useLazyOrderAuthInitQuery } from 'modules/login/api'
import { authRoute } from 'modules/login/routes'
import { decode, getPointPayload, prepareCargoField } from 'modules/order/helpers/order'
import { OrderCreateCargo, OrderCreateRequest } from 'schema'
import { changeFields } from 'shared/forms/mutators'

import { OrderGuestCreateFormMobile } from '../../components/OrderGuestCreateFormMobile'
import OrderGuestCreateForm from '../../components/OrderGuestKDVIDCreateForm'
import { decorators, resetTerminalSyncStates } from '../../components/OrderGuestKDVIDCreateForm/decorators'
import { orderGuestCreateRoute } from '../../routes'
import { TOrderGuestCreateCargoForm } from '../../types/orderGuestCreateTypes'
import { INITIAL_VALUES } from './constants'
import { OrderGuestCreateFormData } from './types'

export const OrderGuestCreatePage: FC = () => {
  const [isShowPhone, setIsShowPhone] = useState(false)
  const searchParams = orderGuestCreateRoute.useSearch()
  const formData = searchParams.data
  const isMobile = useIsMobile()
  const [orderAuthInit] = useLazyOrderAuthInitQuery()
  const navigate = useNavigate()

  const { setFullInfo, clearOrderAddStore } = useActions()

  const initialValues = useMemo(() => {
    if (formData) {
      const { departure, destination, ...rest } = decode(formData)

      const cargo: Partial<OrderCreateCargo> = {
        amount: rest.amount,
        type: rest.type,
        weight: rest.weight,
        length: rest.length,
        width: rest.width,
        height: rest.height
      }
      const cargoItem = prepareCargoField(cargo) as TOrderGuestCreateCargoForm

      return {
        ...INITIAL_VALUES,
        cargoType: OrderCreateRequest.cargoType.SEPARATE,
        cargo: [cargoItem],
        departurePoint: {
          addressDetail: { address: departure }
        },
        destinationPoint: {
          addressDetail: { address: destination }
        }
      } as OrderGuestCreateFormData
    }

    return INITIAL_VALUES
  }, [formData])

  useEffect(() => {
    if (!formData) {
      return
    }

    const { departurePoint, destinationPoint } = initialValues

    setFullInfo(
      getPointPayload({
        departurePoint,
        destinationPoint
      })
    )

    return () => {
      clearOrderAddStore()
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => () => resetTerminalSyncStates(), [])

  const handleClose = useCallback(() => {
    navigate({ to: authRoute.to })
  }, [navigate])

  const handleSuccess = useCallback((widgetUrl: string) => {
    if (widgetUrl) {
      window.location.replace(widgetUrl)
    }

    return undefined
  }, [])

  const handleSubmitOrder = useCallback(
    async (data: OrderGuestCreateFormData) => {
      if (!isShowPhone) {
        setIsShowPhone(true)
      } else if (data?.username) {
        const { username } = data
        sessionStorage.setItem('orderFormData', JSON.stringify(data))

        return orderAuthInit({ username }).unwrap().then(handleSuccess)
      }
    },
    [handleSuccess, isShowPhone, orderAuthInit]
  )

  const form = (
    <OrderGuestCreateForm
      className="grid grid-cols-1 gap-x-6 gap-y-2.5 min-[701px]:grid-cols-[3fr_2fr]"
      isShowPhone={isShowPhone}
    />
  )

  return (
    <>
      <Seo
        description="Создание новой заявки для размещения в сервисе доставки сборных грузов QP5 Express. Заполнение данных о грузе и предварительный расчет стоимости грузоперевозки."
        title="Новая заявка – сборные перевозки QP5 Express"
      />

      <Form<OrderGuestCreateFormData>
        keepDirtyOnReinitialize
        decorators={decorators}
        initialValues={initialValues}
        mutators={{ changeFields }}
        name="createOrderGuestForm"
        onSubmit={handleSubmitOrder}
        render={(formProps) =>
          isMobile ? (
            <OrderGuestCreateFormMobile
              isShowPhone={isShowPhone}
              onClose={handleClose}
              onSubmit={formProps.handleSubmit}
            >
              {form}
            </OrderGuestCreateFormMobile>
          ) : (
            <form
              className="w-[1004px] rounded bg-surface p-4 max-[1052px]:w-full"
              name="createOrderGuestForm"
              onSubmit={formProps.handleSubmit}
            >
              {form}
            </form>
          )
        }
      />
    </>
  )
}
